import { FC, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    FormControl,
    FormControlLabel,
    InputLabel,
    LinearProgress,
    Link,
    MenuItem,
    Paper,
    Select,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import { Mail, Send } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { TintoaUserSearch, type TintoaUserSearchResult } from "@tintoa/ui";
import type { IClient } from "@tintoa/identity-service-client";
import { adminApiClient } from "../utils/adminApiClient";

const TINTOA_CLIENT_NAME = "tintoa";

export const SendMessageManagement: FC = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState<IClient[]>([]);
    const [selectedUser, setSelectedUser] =
        useState<TintoaUserSearchResult | null>(null);
    const [clientId, setClientId] = useState("");
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [footer, setFooter] = useState("");
    const [link, setLink] = useState("");
    const [linkTitle, setLinkTitle] = useState("");
    const [sendMail, setSendMail] = useState(false);

    const tintoaClient = useMemo(
        () => clients.find((client) => client.name === TINTOA_CLIENT_NAME),
        [clients]
    );

    const selectableClients = useMemo(() => {
        const others = clients.filter(
            (client) =>
                !client.isProtected && client.name !== TINTOA_CLIENT_NAME
        );
        return tintoaClient ? [tintoaClient, ...others] : others;
    }, [clients, tintoaClient]);

    useEffect(() => {
        adminApiClient
            .getClients()
            .then((loaded) => {
                setClients(loaded);
                const tintoa = loaded.find(
                    (client) => client.name === TINTOA_CLIENT_NAME
                );
                if (tintoa) {
                    setClientId(tintoa.id);
                }
            })
            .catch((error) => {
                console.error("Error loading clients:", error);
                enqueueSnackbar("Clients konnten nicht geladen werden", {
                    variant: "error"
                });
            });
    }, [enqueueSnackbar]);

    const handleSend = async () => {
        if (!selectedUser) {
            enqueueSnackbar("Bitte einen Empfänger auswählen", {
                variant: "error"
            });
            return;
        }
        if (!clientId) {
            enqueueSnackbar("Bitte eine Client-App auswählen", {
                variant: "error"
            });
            return;
        }
        if (!title.trim() || !content.trim()) {
            enqueueSnackbar("Titel und Inhalt sind Pflichtfelder", {
                variant: "error"
            });
            return;
        }

        try {
            setLoading(true);
            await adminApiClient.createMessage({
                tid: selectedUser.tid,
                title: title.trim(),
                content: content.trim(),
                footer: footer.trim() || undefined,
                link: link.trim() || undefined,
                linkTitle: linkTitle.trim() || undefined,
                sendMail,
                clientId
            });
            enqueueSnackbar("Nachricht erfolgreich gesendet", {
                variant: "success"
            });
            setTitle("");
            setContent("");
            setFooter("");
            setLink("");
            setLinkTitle("");
            setSendMail(false);
            setClientId(tintoaClient?.id ?? "");
            setSelectedUser(null);
        } catch (error) {
            console.error("Error sending message:", error);
            enqueueSnackbar("Nachricht konnte nicht gesendet werden", {
                variant: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link
                    color="inherit"
                    href="/app/profile"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate("/app/profile");
                    }}
                    sx={{
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" }
                    }}
                >
                    Profil
                </Link>
                <Link
                    color="inherit"
                    href="/app/admin"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate("/app/admin");
                    }}
                    sx={{
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" }
                    }}
                >
                    Admin Dashboard
                </Link>
                <Typography color="text.primary">Nachricht senden</Typography>
            </Breadcrumbs>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 3
                }}
            >
                <Mail sx={{ fontSize: 40, color: "primary.main" }} />
                <Box>
                    <Typography variant="h4" component="h1">
                        Nachricht senden
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Sende eine Nachricht an einen bestimmten Nutzer
                    </Typography>
                </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                Standardmäßig wird als Absender die App „tintoa“ verwendet
                (globale Systemnachrichten). Optional kann zusätzlich eine
                E-Mail an den Nutzer geschickt werden.
            </Alert>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Neue Nachricht
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        maxWidth: 720
                    }}
                >
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Empfänger
                        </Typography>
                        <TintoaUserSearch
                            placeholder="Benutzer suchen (mind. 3 Zeichen)…"
                            onSelect={(user) => setSelectedUser(user)}
                        />
                        {selectedUser ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1 }}
                            >
                                Ausgewählt: {selectedUser.firstname}{" "}
                                {selectedUser.lastname} ({selectedUser.email})
                            </Typography>
                        ) : null}
                    </Box>

                    <FormControl fullWidth disabled={loading || !clientId}>
                        <InputLabel>Client-App</InputLabel>
                        <Select
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            label="Client-App"
                        >
                            {selectableClients.map((client) => (
                                <MenuItem key={client.id} value={client.id}>
                                    {client.name === TINTOA_CLIENT_NAME
                                        ? "tintoa (global)"
                                        : client.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Titel"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        required
                        disabled={loading}
                    />

                    <TextField
                        label="Inhalt"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        fullWidth
                        required
                        multiline
                        minRows={4}
                        disabled={loading}
                    />

                    <TextField
                        label="Footer (optional)"
                        value={footer}
                        onChange={(e) => setFooter(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={loading}
                    />

                    <TextField
                        label="Link (optional)"
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                        fullWidth
                        disabled={loading}
                        placeholder="https://…"
                    />

                    <TextField
                        label="Link-Titel (optional)"
                        value={linkTitle}
                        onChange={(e) => setLinkTitle(e.target.value)}
                        fullWidth
                        disabled={loading}
                        placeholder="Link öffnen"
                        helperText="Wird nur verwendet, wenn ein Link gesetzt ist"
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={sendMail}
                                onChange={(e) => setSendMail(e.target.checked)}
                                disabled={loading}
                            />
                        }
                        label="Zusätzlich als E-Mail senden"
                    />

                    <Button
                        variant="contained"
                        startIcon={<Send />}
                        onClick={handleSend}
                        disabled={loading || !selectedUser || !clientId}
                        size="large"
                        sx={{ alignSelf: "flex-start" }}
                    >
                        Nachricht senden
                    </Button>
                </Box>
            </Paper>

            {loading ? <LinearProgress /> : null}
        </Box>
    );
};
